import { styled } from '@mui/material/styles';

export type MoneyKind = 'credit' | 'debit' | 'pending' | 'plain';

const Root = styled('span', {
  shouldForwardProp: (prop) => prop !== 'kind' && prop !== 'hero',
})<{ kind: MoneyKind; hero: boolean }>(({ theme, kind, hero }) => {
  const { color, type, font } = theme.palette.tokens;
  const colorFor: Record<MoneyKind, string> = {
    credit: color.positive,
    debit: color.textPrimary,
    pending: color.textSecondary,
    plain: color.textPrimary,
  };

  return {
    fontVariantNumeric: 'tabular-nums lining-nums',
    fontFeatureSettings: '"tnum" 1, "lnum" 1',
    letterSpacing: '-0.01em',
    color: colorFor[kind],
    whiteSpace: 'nowrap',
    ...(hero && {
      fontSize: `${type.moneyHero.size}px`,
      lineHeight: type.moneyHero.lineHeight,
      fontWeight: type.moneyHero.weight,
    }),
    ...(!hero && { fontWeight: font.weightMedium }),
  };
});

interface MoneyProps {
  value: number | null | undefined;
  kind?: MoneyKind;
  hero?: boolean;
  /** Shows "+" for credits and a typographic minus (U+2212) for debits. */
  signed?: boolean;
  currency?: string;
}

const formatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Money treatment (guide 5.4): tabular digits, pt-BR separators, thin space
 * after the currency symbol, same size for cents, right-aligned in tables by
 * the caller. Debits are neutral, never red.
 */
export function Money({
  value,
  kind = 'plain',
  hero = false,
  signed = false,
  currency = 'R$',
}: MoneyProps): JSX.Element {
  if (value == null) {
    return (
      <Root kind="pending" hero={hero}>
        —
      </Root>
    );
  }

  const absolute = formatter.format(Math.abs(value));
  let sign = '';
  if (signed && value > 0 && kind === 'credit') sign = '+';
  if (signed && (value < 0 || kind === 'debit')) sign = '−';

  return (
    <Root kind={kind} hero={hero}>
      {sign}
      {currency}
      {' '}
      {absolute}
    </Root>
  );
}
